import React, { useState } from 'react';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { loginUserSelector } from '@/redux/user/user.selectors';
import { openLoginModal } from '@/redux/modal/modal.slice';
import { MINUS_ONE, ONE, ZERO } from '@/constants/common';
import { Button } from '@/shared/Button';
import { adminEmailValSelector } from '@/redux/configuration/configuration.selectors';
import { projectsSelector } from '@/redux/projects/projects.selectors';
import { getOAuth } from '@/redux/oauth/oauth.thunks';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { DEFAULT_PROJECT_ID } from '@/constants';
import { LinkButton } from '@/shared/LinkButton';

export const AccessDeniedModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const login = useAppSelector(loginUserSelector);
  const projects = useAppSelector(projectsSelector);

  const [order, setOrder] = useState('projectId');

  const sortedProjects = [...projects].sort((a, b) => {
    if (a.projectId === DEFAULT_PROJECT_ID) {
      return -ONE;
    }
    if (b.projectId === DEFAULT_PROJECT_ID) {
      return ONE;
    }
    if (order === 'projectId') {
      return a.projectId.localeCompare(b.projectId);
    }
    return a.name.localeCompare(b.name);
  });

  const adminEmail = useAppSelector(adminEmailValSelector);

  const isAnonymousLogin = !login;

  const isProjectsAvailable = projects.length > ZERO;

  const isAdminEmail = adminEmail !== '' && adminEmail !== undefined;

  const handleGoBack = async (e: React.FormEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    window.history.go(MINUS_ONE);
  };

  const handleLogin = async (e: React.FormEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    dispatch(getOAuth());
    dispatch(openLoginModal());
  };

  const handleContactAmin = async (e: React.FormEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    window.location.href = `mailto:${adminEmail}`;
  };

  const openProject = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    window.location.href = e.currentTarget.href;
  };

  const sortByProjectId = async (): Promise<void> => {
    setOrder('projectId');
  };

  const sortByName = async (): Promise<void> => {
    setOrder('name');
  };

  return (
    <div className="w-[800px] border border-t-[#E1E0E6] bg-white p-[24px]">
      {isAnonymousLogin && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Button
              className="ring-transparent hover:ring-transparent"
              variantStyles="secondary"
              onClick={handleGoBack}
            >
              Go back to previous page
            </Button>
          </div>
          <div className="text-center">
            <Button className="block w-full" onClick={handleLogin}>
              Login to your account
            </Button>
          </div>
        </div>
      )}
      {isProjectsAvailable && (
        <div>
          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
            <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400 rtl:text-right">
              <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-2.5">
                    <div className="flex items-center">
                      Project Id
                      <LinkButton
                        variant="basic"
                        onClick={sortByProjectId}
                        style={{ cursor: 'pointer' }}
                      >
                        <svg
                          className="ms-1.5 h-3 w-3"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                        </svg>
                      </LinkButton>
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-2.5">
                    <div className="flex items-center">
                      Name
                      <LinkButton
                        onClick={sortByName}
                        variant="basic"
                        style={{ cursor: 'pointer' }}
                      >
                        <svg
                          className="ms-1.5 h-3 w-3"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                        </svg>
                      </LinkButton>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map(project => (
                  <tr
                    key={project.projectId}
                    className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <th
                      scope="row"
                      className="whitespace-nowrap px-6 py-2 font-medium text-gray-900 dark:text-white"
                    >
                      <Link
                        key={project.projectId}
                        className={twMerge(
                          'text-center text-blue-800',
                          project.projectId === DEFAULT_PROJECT_ID && 'font-bold',
                        )}
                        href={`?id=${project.projectId}`}
                        onClick={openProject}
                      >
                        {project.projectId === DEFAULT_PROJECT_ID && (
                          <span style={{ fontSize: '1.2em' }}>&#8226; </span>
                        )}
                        {project.projectId}
                      </Link>
                    </th>
                    <td className="px-6 py-2">{project.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {isAdminEmail && (
        <div className="mt-1 text-center">
          <Button
            className="block w-full ring-transparent hover:ring-transparent"
            variantStyles="secondary"
            onClick={handleContactAmin}
          >
            Contact admin
          </Button>
        </div>
      )}
    </div>
  );
};
